import { supabase, handleError, type ServiceResult, type PaginatedResult, getPaginationRange } from '@/services/base.service';
import type { AdminUser, UserFilters } from '../types';

export const ROLE_MAP: Record<string, string> = {
  'super_admin': 'Quản trị viên tối cao',
  'admin': 'Quản trị viên',
  'director': 'Giám đốc',
  'pm': 'Quản lý dự án',
  'qa_qc': 'Kỹ sư QA/QC',
  'qs': 'Kỹ sư QS',
  'site_manager': 'Chỉ huy trưởng',
  'warehouse': 'Thủ kho',
  'accountant': 'Kế toán',
  'secretary': 'Thư ký dự án',
  'supervisor': 'Giám sát',
  'staff': 'Nhân viên',
  'site_foreman': 'Đội trưởng',
  'equipment_manager': 'Quản lý thiết bị',
  'zone_manager': 'Quản lý khu vực',
  'agro_manager': 'Kỹ sư cảnh quan'
};

export const adminUsersService = {
  /** List all users across all companies (Super Admin only) */
  async list(filters: any = {}): Promise<PaginatedResult<AdminUser>> {
    try {
      const { from, to } = getPaginationRange(filters.page || 1, filters.pageSize || 20);

      // 1. Fetch Users first (without Join to avoid schema errors)
      let query = supabase
        .from('user_profiles')
        .select('*', { count: 'exact' });

      if (filters.company_id) query = query.eq('company_id', filters.company_id);
      if (filters.role_code) query = query.eq('role_code', filters.role_code);
      if (filters.is_active !== undefined) query = query.eq('is_active', filters.is_active);
      if (filters.search) {
        query = query.or(`full_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
      }

      query = query.range(from, to).order('created_at', { ascending: false });

      const { data: users, error: userError, count } = await query;

      if (userError) {
        console.error('AdminUsersService.list users error:', userError);
        throw userError;
      }

      if (!users || users.length === 0) {
        return { data: [], count: 0, error: null };
      }

      // 2. Fetch Companies for these users
      const companyIds = Array.from(new Set(users.map(u => u.company_id).filter(Boolean)));
      let companyMap: Record<string, any> = {};

      if (companyIds.length > 0) {
        const { data: companies, error: companyError } = await supabase
          .from('companies')
          .select('id, name, code')
          .in('id', companyIds);

        if (!companyError && companies) {
          companies.forEach(c => {
            companyMap[c.id] = c;
          });
        }
      }

      // 3. Transform and Map
      const transformed = users.map((u: any) => {
        // Normalization for legacy data
        let normalizedRoleCode = u.role_code;
        if (normalizedRoleCode === 'maintenance_staff') normalizedRoleCode = 'staff';
        if (normalizedRoleCode === 'maintenance_supervisor') normalizedRoleCode = 'supervisor';

        return {
          ...u,
          role_code: normalizedRoleCode, // Normalize for the UI
          company_name: companyMap[u.company_id]?.name || 'Cá nhân',
          company_code: companyMap[u.company_id]?.code || 'PERSONAL',
          role_name: ROLE_MAP[normalizedRoleCode as keyof typeof ROLE_MAP] || normalizedRoleCode
        };
      });

      return { data: transformed as AdminUser[], count: count || 0, error: null };
    } catch (err) {
      return { data: [], count: 0, error: handleError(err) };
    }
  },

  /** Get single user detail */
  async getById(id: string): Promise<ServiceResult<AdminUser>> {
    try {
      const { data: user, error: userError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', id)
        .single();

      if (userError) throw userError;

      let companyName = 'Cá nhân';
      let companyCode = 'PERSONAL';

      if (user.company_id) {
        const { data: company } = await supabase
          .from('companies')
          .select('name, code')
          .eq('id', user.company_id)
          .single();
        
        if (company) {
          companyName = company.name;
          companyCode = company.code;
        }
      }

      // Normalization for legacy data
      let normalizedRoleCode = user.role_code;
      if (normalizedRoleCode === 'maintenance_staff') normalizedRoleCode = 'staff';
      if (normalizedRoleCode === 'maintenance_supervisor') normalizedRoleCode = 'supervisor';

      return { 
        data: {
          ...user,
          role_code: normalizedRoleCode,
          company_name: companyName,
          company_code: companyCode,
          role_name: ROLE_MAP[normalizedRoleCode as keyof typeof ROLE_MAP] || normalizedRoleCode
        } as AdminUser, 
        error: null 
      };
    } catch (err) {
      return { data: null as any, error: handleError(err) };
    }
  },

  /** Create new user (Admin only) */
  async create(input: any): Promise<ServiceResult<AdminUser>> {
    try {
      const payload = { ...input };
      if (payload.company_id === '') payload.company_id = null;

      const { data, error } = await supabase
        .from('user_profiles')
        .insert([{
          ...payload,
          id: crypto.randomUUID(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();
      
      if (error) throw error;
      return { data: data as AdminUser, error: null };
    } catch (err) {
      return { data: null as any, error: handleError(err) };
    }
  },

  /** Update user profile (Admin only) */
  async update(id: string, updates: Partial<AdminUser>): Promise<ServiceResult<void>> {
    try {
      // Remove enriched fields and sensitive/immutable fields that don't exist in DB or shouldn't be updated here
      const { company_name, company_code, role_name, email, permissions, ...validUpdates } = updates as any;
      
      // Normalize empty strings to null for UUID fields
      if (validUpdates.company_id === '') {
        validUpdates.company_id = null;
      }
      
      const { error } = await supabase
        .from('user_profiles')
        .update({
          ...validUpdates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);
      if (error) throw error;
      return { data: null, error: null };
    } catch (err) {
      return { data: null, error: handleError(err) };
    }
  },

  /** Toggle lock status */
  async toggleLock(id: string, isActive: boolean): Promise<ServiceResult<void>> {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ is_active: isActive })
        .eq('id', id);
      if (error) throw error;
      return { data: null, error: null };
    } catch (err) {
      return { data: null, error: handleError(err) };
    }
  },

  /** Get user audit logs */
  async getAuditLogs(userId: string): Promise<ServiceResult<any[]>> {
    try {
      const { data, error } = await supabase
        .from('audit_logs')
        .select('*')
        .eq('performed_by', userId)
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (err: any) {
      // Fallback if table doesn't exist to avoid 400 crash
      return { data: [], error: null };
    }
  }
};
